import { useState } from 'react';
import { yearEndApi } from '../../api/services';
import type { YearEndCloseDto, YearEndLineDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Loadable, Modal, NumberInput, PageHeader, Select, Stat, Table, fmtDate, fmtDateTime, fmtMoney, money, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';
import { PERM, useCan } from '../../ui/access';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** Cierre anual: traslada la utilidad o pérdida del año a la cuenta de resultados acumulados. */
export function YearEndPage() {
  const closes = useAsync(() => yearEndApi.list(), []);
  const canClose = useCan(PERM.periodClose);
  const [wizard, setWizard] = useState(false);
  const { run, busy } = useAction();

  const cancel = (c: YearEndCloseDto) => run(async (reason) => {
    await yearEndApi.cancel(c.id, reason ?? '');
    closes.reload();
  }, {
    confirm: `Se anulará el cierre ${fmtDate(c.dateFrom)} – ${fmtDate(c.dateTo)} con un asiento de reverso. El período de ese mes debe estar abierto. Indique el motivo.`,
    danger: true, requireReason: true, success: 'Cierre anual anulado.',
  });

  return (
    <>
      <PageHeader title="Cierre anual" subtitle="Traslado del resultado del año a patrimonio"
        actions={canClose ? <Button kind="primary" onClick={() => setWizard(true)}>Nuevo cierre anual…</Button> : undefined} />
      <PageHelp page="yearEnd" />
      <Loadable state={closes}>
        {(rows) => (
          <Table rows={rows} rowKey={(c) => c.id} empty="Todavía no hay cierres anuales." columns={[
            { key: 'p', title: 'Año fiscal', render: (c) => `${fmtDate(c.dateFrom)} – ${fmtDate(c.dateTo)}` },
            money<YearEndCloseDto>('i', 'Ingresos', (c) => c.totalIncome),
            money<YearEndCloseDto>('c', 'Costos', (c) => c.totalCost),
            money<YearEndCloseDto>('g', 'Gastos', (c) => c.totalExpense),
            { key: 'r', title: 'Resultado', align: 'right', render: (c) => <span style={c.result < 0 ? { color: '#a1271a' } : undefined}>{fmtMoney(c.result)}</span> },
            { key: 'a', title: 'Asiento', render: (c) => <Link to={`/entries/${c.entryCode}`}>{c.entryCode}</Link> },
            { key: 's', title: 'Estado', render: (c) => c.status === 'A'
              ? <Badge tone="ok">Vigente</Badge>
              : <span><Badge tone="bad">Anulado</Badge> {c.cancellationEntryCode && <>reverso <Link to={`/entries/${c.cancellationEntryCode}`}>{c.cancellationEntryCode}</Link></>}</span> },
            { key: 'f', title: 'Registrado', render: (c) => fmtDateTime(c.createdOn) },
            { key: 'x', title: '', render: (c) => (c.status === 'A' && canClose ? <Button kind="link" busy={busy} onClick={() => cancel(c)}>Anular…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {wizard && <YearEndWizard onClose={() => setWizard(false)} onDone={() => { setWizard(false); closes.reload(); }} />}
    </>
  );
}

function YearEndWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [month, setMonth] = useState(12);
  const [asked, setAsked] = useState<{ year: number; month: number } | null>(null);
  const preview = useAsync(() => yearEndApi.preview(asked!.year, asked!.month), [asked?.year, asked?.month], asked !== null);
  const { run, busy } = useAction();

  return (
    <Modal title="Nuevo cierre anual" onClose={onClose} size="lg">
      <div className="acc-toolbar">
        <Field label="Año en que termina el año fiscal"><NumberInput step="1" value={year} onChange={(v) => setYear(v || now.getFullYear())} /></Field>
        <Field label="Mes de cierre"><Select value={month} onChange={setMonth} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
        <Button onClick={() => setAsked({ year, month })}>Revisar</Button>
      </div>
      {asked === null && <Alert kind="info">Elija el año y el mes en que termina su año fiscal y pulse "Revisar". Todavía no se registra nada.</Alert>}
      {asked !== null && (
        <Loadable state={preview}>
          {(p) => (
            <>
              <p>Año fiscal del <strong>{fmtDate(p.dateFrom)}</strong> al <strong>{fmtDate(p.dateTo)}</strong> · montos en {p.currency}.</p>
              <div className="acc-stats">
                <Stat label="Ingresos" value={fmtMoney(p.totalIncome)} />
                <Stat label="Costos" value={fmtMoney(p.totalCost)} />
                <Stat label="Gastos" value={fmtMoney(p.totalExpense)} />
                <Stat label={p.result >= 0 ? 'Utilidad del año' : 'Pérdida del año'} value={fmtMoney(Math.abs(p.result))} tone={p.result >= 0 ? 'pos' : 'neg'} />
              </div>
              {p.retainedEarningsAccount && (
                <p>El resultado se traslada a <strong>{p.retainedEarningsAccount} · {p.retainedEarningsAccountName}</strong>.</p>
              )}
              {p.blockers.map((b) => <Alert key={b} kind="error">{b}</Alert>)}
              {p.blockers.some((b) => b.includes('Parámetros contables')) && <p><Link to="/settings">Ir a Parámetros contables</Link></p>}
              {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
              {p.lines.length > 0 && (
                <Card title="Asiento que se registrará">
                  <Table rows={p.lines} rowKey={(l) => `${l.accountNumber}|${l.customerCode ?? ''}`} columns={[
                    { key: 'n', title: 'Cuenta', render: (l) => `${l.accountNumber} · ${l.accountName}` },
                    { key: 's', title: 'Grupo', render: (l) => l.section },
                    { key: 'c', title: 'Cliente', render: (l) => l.customerCode ?? '' },
                    money<YearEndLineDto>('d', 'Débito', (l) => (l.debit ? l.debit : null)),
                    money<YearEndLineDto>('h', 'Crédito', (l) => (l.credit ? l.credit : null)),
                  ]} footer={
                    <tr>
                      <td colSpan={3}><strong>Totales</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{fmtMoney(p.lines.reduce((a, l) => a + l.debit, 0))}</strong></td>
                      <td style={{ textAlign: 'right' }}><strong>{fmtMoney(p.lines.reduce((a, l) => a + l.credit, 0))}</strong></td>
                    </tr>
                  } />
                </Card>
              )}
              {p.canClose && <Alert kind="info">El asiento queda aplicado en el período {MONTHS[asked.month - 1].toLowerCase()} {asked.year}. Si después necesita corregir algo del año, anule este cierre (se registra su reverso) y vuelva a generarlo.</Alert>}
              <div className="acc-actions">
                <Button onClick={onClose}>Cancelar</Button>
                <Button kind="primary" disabled={!p.canClose} busy={busy} onClick={() => run(async () => {
                  await yearEndApi.close(asked.year, asked.month);
                  onDone();
                }, { confirm: `Confirma el cierre anual al ${fmtDate(p.dateTo)}?`, success: 'Cierre anual registrado.' })}>Registrar cierre anual</Button>
              </div>
            </>
          )}
        </Loadable>
      )}
    </Modal>
  );
}
